import { Box, IconButton, Typography, useTheme } from "@mui/material";
import { KeyboardArrowLeft, KeyboardArrowRight } from "@mui/icons-material";
import React, { useEffect, useState } from "react";
import { useBannerContext } from "../../../shared/contexts/BannerContext";
import { formatImageSrc } from "../../../shared/utils/image-utils";

export const DashboardBannerCarousel: React.FC = () => {
    const theme = useTheme();
    const { banners, showBanners, isLoading } = useBannerContext();
    const [activeStep, setActiveStep] = useState(0);
    const maxSteps = banners.length;

    // Auto-play
    useEffect(() => {
        if (!showBanners || maxSteps <= 1) return;

        const timer = setInterval(() => {
            setActiveStep((prevActiveStep) => (prevActiveStep + 1) % maxSteps);
        }, 5000); // 5 seconds

        return () => {
            clearInterval(timer);
        };
    }, [showBanners, maxSteps]);

    const handleNext = () => {
        setActiveStep((prevActiveStep) => (prevActiveStep + 1) % maxSteps);
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => (prevActiveStep - 1 + maxSteps) % maxSteps);
    };

    if (!showBanners || maxSteps === 0) {
        return null;
    }

    const currentBanner = banners[activeStep];

    return (
        <Box sx={{ maxWidth: '100%', flexGrow: 1, mb: 2, position: 'relative', borderRadius: 4, overflow: 'hidden', boxShadow: 3 }}>
            <Box
                sx={{
                    width: '100%',
                    aspectRatio: '1200 / 220',
                    display: 'block',
                    position: 'relative',
                    backgroundColor: '#ffffff',
                }}
            >
                {/* Clickable Area */}
                <Box
                    component={currentBanner.link ? 'a' : 'div'}
                    href={currentBanner.link || undefined}
                    target={currentBanner.link ? "_blank" : undefined}
                    rel="noopener noreferrer"
                    sx={{
                        display: 'block',
                        width: '100%',
                        height: '100%',
                        textDecoration: 'none',
                        cursor: currentBanner.link ? 'pointer' : 'default',
                    }}
                >
                    <Box
                        component="img"
                        sx={{
                            height: '100%',
                            width: '100%',
                            display: 'block',
                            objectFit: 'cover',
                        }}
                        src={formatImageSrc(currentBanner.image || currentBanner.imageUrl)}
                        alt={currentBanner.title}
                    />

                    {/* Gradient Overlay for Title */}
                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            width: '100%',
                            background: 'linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0) 100%)',
                            p: 3,
                            pt: 6,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'flex-end',
                        }}
                    >
                        <Typography variant="h5" color="white" fontWeight="bold" sx={{ textShadow: '0px 2px 4px rgba(0,0,0,0.5)' }}>
                            {currentBanner.title}
                        </Typography>
                    </Box>
                </Box>

                {/* Navigation Bars */}
                {maxSteps > 1 && (
                    <>
                        {/* Left Nav Bar */}
                        <Box
                            onClick={(e) => { e.preventDefault(); handleBack(); }}
                            sx={{
                                position: 'absolute',
                                left: 0,
                                top: 0,
                                height: '100%',
                                width: '50px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                transition: 'background-color 0.3s',
                                '&:hover': {
                                    backgroundColor: 'rgba(255,255,255,0.2)',
                                },
                                zIndex: 2,
                            }}
                        >
                            <IconButton
                                size="small"
                                sx={{
                                    color: 'white',
                                    bgcolor: 'rgba(0,0,0,0.3)',
                                    '&:hover': { bgcolor: 'rgba(0,0,0,0.5)' }
                                }}
                            >
                                <KeyboardArrowLeft fontSize="large" />
                            </IconButton>
                        </Box>

                        {/* Right Nav Bar */}
                        <Box
                            onClick={(e) => { e.preventDefault(); handleNext(); }}
                            sx={{
                                position: 'absolute',
                                right: 0,
                                top: 0,
                                height: '100%',
                                width: '50px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                transition: 'background-color 0.3s',
                                '&:hover': {
                                    backgroundColor: 'rgba(255,255,255,0.2)',
                                },
                                zIndex: 2,
                            }}
                        >
                            <IconButton
                                size="small"
                                sx={{
                                    color: 'white',
                                    bgcolor: 'rgba(0,0,0,0.3)',
                                    '&:hover': { bgcolor: 'rgba(0,0,0,0.5)' }
                                }}
                            >
                                <KeyboardArrowRight fontSize="large" />
                            </IconButton>
                        </Box>
                    </>
                )}

                {/* Dots Indicator */}
                {maxSteps > 1 && (
                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: 16,
                            right: 16,
                            display: 'flex',
                            gap: 1,
                            zIndex: 2,
                        }}
                    >
                        {banners.map((_, index) => (
                            <Box
                                key={index}
                                onClick={(e) => {
                                    e.preventDefault();
                                    setActiveStep(index);
                                }}
                                sx={{
                                    width: 10,
                                    height: 10,
                                    borderRadius: '50%',
                                    bgcolor: index === activeStep ? 'white' : 'rgba(255,255,255,0.5)',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s',
                                    boxShadow: '0px 1px 2px rgba(0,0,0,0.3)',
                                    '&:hover': {
                                        bgcolor: 'white',
                                        transform: 'scale(1.2)',
                                    }
                                }}
                            />
                        ))}
                    </Box>
                )}
            </Box>
        </Box>
    );
};
